<template>
  <div class="setting-card" :style="{ '--accent': '#4F7EB3' }">
    <div class="setting-card-head">
      <span class="sc-accent"><i class="bi bi-clock"></i></span>
      <div class="sc-heading">
        <h2 class="sc-title">{{ $t('openingHours.title') }}</h2>
        <p class="sc-desc">{{ $t('openingHours.desc') }}</p>
      </div>
    </div>

    <div class="setting-card-body">
      <!-- Info notice -->
      <div v-if="showDefaultNotice" class="alert info">
        <i class="bi bi-info-circle"></i>
        {{ $t('openingHours.defaultNotice') }}
      </div>

      <!-- Regular Weekly Hours — compact one-row-per-day -->
      <div class="oh-section">
        <div class="oh-section-head">
          <span class="oh-section-title">{{ $t('openingHours.regularTitle') }}</span>
        </div>
        <div class="oh-list">
          <div class="oh-row" v-for="day in weekdays" :key="day">
            <span class="oh-day">{{ $t(`openingHours.dayShort.${day}`) }}</span>
            <label class="oh-switch">
              <input class="form-check-input" type="checkbox" role="switch"
                     :id="`open-${day}`" v-model="normalHours[day].isOpen"
                     @change="() => handleOpenToggle(day)" />
              <span>{{ normalHours[day].isOpen ? $t('openingHours.open') : $t('openingHours.closed') }}</span>
            </label>

            <template v-if="normalHours[day].isOpen">
              <label class="oh-switch sm">
                <input class="form-check-input" type="checkbox" role="switch"
                       :id="`is24h-${day}`" v-model="normalHours[day].is24h"
                       @change="() => handleIs24Toggle(day)" />
                <span>{{ $t('openingHours.allDay') }}</span>
              </label>

              <div v-if="!normalHours[day].is24h" class="oh-times">
                <input type="time" v-model="normalHours[day].open" />
                <span>–</span>
                <input type="time" v-model="normalHours[day].close" />
              </div>

              <label v-if="!normalHours[day].is24h" class="oh-switch sm">
                <input class="form-check-input" type="checkbox" role="switch"
                       :id="`lunch-${day}`" v-model="normalHours[day].hasLunch"
                       @change="() => handleLunchToggle(day)" />
                <span>{{ $t('openingHours.lunch') }}</span>
              </label>

              <div v-if="normalHours[day].hasLunch && !normalHours[day].is24h" class="oh-times">
                <span class="oh-lbl">{{ $t('openingHours.lunch') }}</span>
                <input type="time" v-model="normalHours[day].lunchStart" />
                <span>–</span>
                <input type="time" v-model="normalHours[day].lunchEnd" />
              </div>
            </template>
          </div>
        </div>

        <!-- From when the new weekly hours apply. Earlier days keep the hours they had,
             unless the old hours were simply wrong ("I corrected a mistake"). -->
        <div v-if="isRegularChanged" class="oh-valid" role="radiogroup" :aria-label="$t('openingHours.applyTitle')">
          <span class="oh-valid-title">{{ $t('openingHours.applyTitle') }}</span>
          <label class="oh-valid-opt"><input type="radio" value="today" v-model="validChoice"> {{ $t('openingHours.applyToday') }}</label>
          <label class="oh-valid-opt">
            <input type="radio" value="date" v-model="validChoice"> {{ $t('openingHours.applyFrom') }}
            <input type="date" class="oh-valid-date" v-model="validDate" @focus="validChoice = 'date'">
          </label>
          <label class="oh-valid-opt"><input type="radio" value="always" v-model="validChoice"> {{ $t('openingHours.applyAlways') }}</label>
          <p class="oh-hint">
            {{ validChoice === 'always'
              ? $t('openingHours.applyAlwaysHint')
              : $t('openingHours.applyKeepHint') }}
          </p>
        </div>

        <div class="form-actions">
          <button class="btn-solid ok" @click="saveRegularHours" :disabled="!isRegularChanged || (validChoice === 'date' && !validDate)">
            <i class="bi bi-save"></i> {{ $t('openingHours.saveRegular') }}
          </button>
        </div>
      </div>

      <!-- The weekly hours' history (each save of new hours adds a version). -->
      <details v-if="historyRows.length > 1" class="oh-details">
        <summary>{{ $t('openingHours.historyTitle') }} <span class="count">· {{ historyRows.length }}</span></summary>
        <div class="oh-sub-body">
          <div v-for="row in historyRows" :key="row.validFrom || 'start'" class="oh-history-row">
            <span class="oh-history-when">{{ row.when }}</span>
            <span class="oh-history-sum">{{ row.summary }}</span>
            <span v-if="row.current" class="oh-history-tag">{{ $t('openingHours.historyNow') }}</span>
            <span v-else-if="row.upcoming" class="oh-history-tag upcoming">{{ $t('openingHours.historyUpcoming') }}</span>
          </div>
        </div>
      </details>

      <!-- Irregular Opening Hours — collapsible, compact, scroll-capped -->
      <details class="oh-details">
        <summary>
          <i class="bi bi-calendar-week"></i> {{ $t('openingHours.irregularTitle') }}
          <span class="count" v-if="irregularHours.length">· {{ irregularHours.length }}</span>
          <i class="bi bi-chevron-right chev"></i>
        </summary>
        <div class="oh-sub-body">
          <p class="oh-hint">{{ $t('openingHours.irregularHint') }}</p>
          <div class="oh-list" v-if="irregularHours.length">
            <div class="oh-row" v-for="(entry, idx) in irregularHours" :key="idx">
              <Tooltip class="remove-wrap" :text="$t('openingHours.removeDay')" placement="left" cursor="pointer">
                <button class="remove" @click="removeIrregularHour(idx)">×</button>
              </Tooltip>
              <Datepicker
                v-model="entry.date"
                :placeholder="$t('openingHours.selectDate')"
                :lower-limit="yearAgo"
                :upper-limit="yearAhead"
                :input-format="'yyyy-MM-dd'"
                :typeable="true"
                class="datepicker-inline"
              />
              <template v-if="entry.date">
                <label class="oh-switch sm">
                  <input class="form-check-input" type="checkbox" role="switch"
                         :id="`irr-24h-${idx}`" v-model="entry.is24h"
                         @change="() => handleIrregularOpenToggle(entry)" />
                  <span>{{ $t('openingHours.allDay') }}</span>
                </label>
                <div v-if="!entry.is24h" class="oh-times">
                  <input type="time" v-model="entry.open" />
                  <span>–</span>
                  <input type="time" v-model="entry.close" />
                </div>
                <label v-if="!entry.is24h" class="oh-switch sm">
                  <input class="form-check-input" type="checkbox" role="switch"
                         :id="`irr-lunch-${idx}`" v-model="entry.hasLunch"
                         @change="() => handleIrregularLunchToggle(entry)" />
                  <span>{{ $t('openingHours.lunch') }}</span>
                </label>
                <div v-if="entry.hasLunch && !entry.is24h" class="oh-times">
                  <span class="oh-lbl">{{ $t('openingHours.lunch') }}</span>
                  <input type="time" v-model="entry.lunchStart" />
                  <span>–</span>
                  <input type="time" v-model="entry.lunchEnd" />
                </div>
              </template>
            </div>
          </div>

          <div class="oh-foot">
            <button type="button" class="btn-outline sm" @click="addIrregularHour">
              <i class="bi bi-plus-lg"></i> {{ $t('openingHours.addDay') }}
            </button>
            <button class="btn-solid ok" @click="saveIrregularHours" :disabled="!isIrregularChanged">
              <i class="bi bi-save"></i> {{ $t('openingHours.save') }}
            </button>
          </div>
        </div>
      </details>

      <!-- Closing Days — collapsible, compact, scroll-capped -->
      <details class="oh-details">
        <summary>
          <i class="bi bi-calendar-x"></i> {{ $t('openingHours.closingTitle') }}
          <span class="count" v-if="closingDays.length">· {{ closingDays.length }}</span>
          <i class="bi bi-chevron-right chev"></i>
        </summary>
        <div class="oh-sub-body">
          <p class="oh-hint">{{ $t('openingHours.closingHint') }}</p>
          <div class="oh-list" v-if="closingDays.length">
            <div class="oh-row" v-for="(entry, idx) in closingDays" :key="idx">
              <Tooltip class="remove-wrap" :text="$t('openingHours.removeClosing')" placement="left" cursor="pointer">
                <button class="remove" @click="removeClosingDay(idx)">×</button>
              </Tooltip>
              <Datepicker
                v-model="entry.closingStartDate"
                :placeholder="entry.useRange ? $t('openingHours.startDate') : $t('openingHours.date')"
                :lower-limit="yearAgo"
                :upper-limit="yearAhead"
                :input-format="'yyyy-MM-dd'"
                :typeable="true"
                class="datepicker-inline"
              />
              <Datepicker
                v-if="entry.useRange"
                v-model="entry.closingEndDate"
                :placeholder="$t('openingHours.endDate')"
                :lower-limit="yearAgo"
                :upper-limit="yearAhead"
                :input-format="'yyyy-MM-dd'"
                :typeable="true"
                class="datepicker-inline"
              />
              <label class="oh-switch sm">
                <input class="form-check-input" type="checkbox"
                       :id="`closing-range-${idx}`" v-model="entry.useRange" />
                <span>{{ $t('openingHours.dateRange') }}</span>
              </label>
            </div>
          </div>

          <div class="oh-foot">
            <button type="button" class="btn-outline sm" @click="addClosingDay">
              <i class="bi bi-calendar-event"></i> {{ $t('openingHours.addClosing') }}
            </button>
            <button
              class="btn-solid ok"
              @click="saveClosingDays"
              :disabled="!isClosingChanged || !allClosingDatesValid"
            >
              <i class="bi bi-save"></i> {{ $t('openingHours.save') }}
            </button>
          </div>
        </div>
      </details>
    </div>
  </div>
</template>

<script>
    import { reactive, ref, toRaw, onMounted, watch, computed } from 'vue'
    import { versionRows } from '@/settings/openingHoursVersions'
    import { parseISO } from 'date-fns'

    import { useToast } from 'vue-toastification'
    import { i18n } from '@/i18n'
    
    import Datepicker from 'vue3-datepicker'

    import { useOpeningHoursStore } from '@/stores/OpeningHoursStore'
    import Tooltip from '@/components/ui/Tooltip.vue'

    export default {
        name: 'OpeningHours',

        components: {
          Datepicker,
          Tooltip,
        },

        setup () {
            const store = useOpeningHoursStore()
            const toast = useToast()

            const showDefaultNotice = ref(false)

            const weekdays = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday']

            const normalHours = reactive({})
            const irregularHours = ref([])
            const closingDays = ref([])

            const initialRegular = ref({})
            const initialIrregular = ref([])
            const initialClosings = ref([])

            const isRegularChanged = ref(false)
            // From when new weekly hours apply: 'today' | 'date' (validDate) | 'always'.
            const validChoice = ref('today')
            const validDate = ref('')
            const _todayIso = () => {
              const d = new Date()
              return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
            }
            const historyRows = computed(() => versionRows(store.regularHoursVersions, _todayIso()))
            const isIrregularChanged = ref(false)
            const isClosingChanged = ref(false)

            const yearAgo = ref(new Date())
            const yearAhead = ref(new Date())
            yearAgo.value.setFullYear(yearAgo.value.getFullYear() - 2)
            yearAhead.value.setFullYear(yearAhead.value.getFullYear() + 10)

            const deepClone = (obj) => JSON.parse(JSON.stringify(obj))

            weekdays.forEach(day => {
              normalHours[day] = {
                isOpen: !['Saturday', 'Sunday'].includes(day),
                is24h: false,
                open: '',
                close: '',
                hasLunch: false,
                lunchStart: '',
                lunchEnd: ''
              }
            })

            onMounted(async () => {
              const isEmpty = await store.fetchOpeningHours()

              if (!isEmpty) {
                const backendHours = store.regularHours
                weekdays.forEach(day => {
                  if (backendHours[day]) {
                    Object.assign(normalHours[day], backendHours[day])
                  }
                })

                // Convert strings to Date objects
                irregularHours.value = (store.irregularHours || []).map(entry => ({
                  ...entry,
                  date: typeof entry.date === 'string' ? parseISO(entry.date) : entry.date,
                  lunchStart: entry.lunchStart,
                  lunchEnd: entry.lunchEnd
                }))

                closingDays.value = (store.closingDays || []).map(entry => ({
                  ...entry,
                  closingStartDate: typeof entry.closingStartDate === 'string' ? parseISO(entry.closingStartDate) : entry.closingStartDate,
                  closingEndDate: typeof entry.closingEndDate === 'string' && entry.closingEndDate ? parseISO(entry.closingEndDate) : entry.closingEndDate
                }))

                // Store deep copies for comparison
                initialRegular.value = deepClone(normalHours)
                initialIrregular.value = deepClone(irregularHours.value)
                initialClosings.value = deepClone(closingDays.value)
              } else {
                showDefaultNotice.value = true
              }
            })

            watch(() => deepClone(normalHours), (val) => {
              isRegularChanged.value = JSON.stringify(val) !== JSON.stringify(initialRegular.value)
            }, { deep: true })

            watch(irregularHours, (val) => {
              isIrregularChanged.value = JSON.stringify(val) !== JSON.stringify(initialIrregular.value)
            }, { deep: true })

            watch(closingDays, (val) => {
              closingDays.value.forEach(entry => {
                if (!entry.useRange) {
                  entry.closingEndDate = null
                }
              })
              isClosingChanged.value = JSON.stringify(val) !== JSON.stringify(initialClosings.value)
            }, { deep: true })

            watch(closingDays, (newVal) => {
              newVal.forEach(entry => {
                if (entry.useRange && entry.closingStartDate && entry.closingEndDate && entry.closingStartDate > entry.closingEndDate) {
                  toast.error(i18n.global.t('openingHours.endBeforeStart'))
                  entry.closingEndDate = null  // Clear invalid end date immediately
                }
              })
            }, { deep: true })

            const allClosingDatesValid = computed(() => {
              return closingDays.value.every(entry => {
                if (!entry.closingStartDate) return false
                if (entry.useRange && !entry.closingEndDate) return false
                if (entry.useRange && entry.closingStartDate > entry.closingEndDate) return false
                return true
              })
            })

            const expandDateRange = (start, end) => {
              const dates = []
              const current = new Date(start)
              const final = new Date(end)
              while (current <= final) {
                dates.push(new Date(current))
                current.setDate(current.getDate() + 1)
              }
              return dates
            }

            const getExpandedClosingDates = () => {
              const expanded = []
              for (const entry of closingDays.value) {
                if (entry.closingStartDate && !entry.useRange) {
                  expanded.push(entry.closingStartDate.toISOString())
                } else if (entry.useRange && entry.closingStartDate && entry.closingEndDate) {
                  const range = expandDateRange(entry.closingStartDate, entry.closingEndDate)
                  expanded.push(...range.map(d => d.toISOString()))
                }
              }
              return expanded
            }

            const handleOpenToggle = (day) => {
              if (!normalHours[day].isOpen) {
                normalHours[day].open = ''
                normalHours[day].close = ''
                normalHours[day].is24h = false
                normalHours[day].hasLunch = false
                normalHours[day].lunchStart = ''
                normalHours[day].lunchEnd = ''
              }
            }

            const handleIs24Toggle = (day) => {
              if (normalHours[day].is24h) {
                normalHours[day].open = '00:00:00'
                normalHours[day].close = '23:59:59'
                normalHours[day].hasLunch = false
                normalHours[day].lunchStart = ''
                normalHours[day].lunchEnd = ''
              } else {
                normalHours[day].open = ''
                normalHours[day].close = ''
              }
            }

            const handleLunchToggle = (day) => {
              if (!normalHours[day].hasLunch) {
                normalHours[day].lunchStart = ''
                normalHours[day].lunchEnd = ''
              }
            }

            const handleIrregularOpenToggle = (entry) => {
              if (entry.is24h) {
                entry.open = '00:00:00'
                entry.close = '23:59:59'
                entry.hasLunch = false
                entry.lunchStart = ''
                entry.lunchEnd = ''
              } else {
                entry.open = ''
                entry.close = ''
              }
            }

            const handleIrregularLunchToggle = (entry) => {
              if (!entry.hasLunch) {
                entry.lunchStart = ''
                entry.lunchEnd = ''
              }
            }

            const saveRegularHours = async () => {
              weekdays.forEach(day => {
                if (normalHours[day].isOpen) {
                  if (normalHours[day].is24h) {
                    normalHours[day].open = '00:00:00'
                    normalHours[day].close = '23:59:59'
                  } else {
                    if (normalHours[day].open && normalHours[day].open.length === 5) {
                      normalHours[day].open += ':00'
                    }
                    if (normalHours[day].close && normalHours[day].close.length === 5) {
                      normalHours[day].close += ':00'
                    }
                    if (normalHours[day].hasLunch) {
                      if (normalHours[day].lunchStart && normalHours[day].lunchStart.length === 5) {
                        normalHours[day].lunchStart += ':00'
                      }
                      if (normalHours[day].lunchEnd && normalHours[day].lunchEnd.length === 5) {
                        normalHours[day].lunchEnd += ':00'
                      }
                    }
                  }
                }
              })

              await store.saveOpeningHours({
                regularHours: toRaw(normalHours),
                irregularHours: toRaw(irregularHours.value),
                closingDays: toRaw(closingDays.value),
                validFrom: validChoice.value === 'date' ? validDate.value : validChoice.value,
              })
              validChoice.value = 'today'
              validDate.value = ''

              await store.fetchOpeningHours()
              showDefaultNotice.value = false

              initialRegular.value = deepClone(normalHours)
              isRegularChanged.value = false

              toast.success(i18n.global.t('openingHours.savedRegular'))
            }

            const saveIrregularHours = async () => {
              irregularHours.value.forEach(entry => {
                if (entry.is24h) {
                  entry.open = '00:00:00'
                  entry.close = '23:59:59'
                } else {
                  if (entry.open && entry.open.length === 5) {
                    entry.open += ':00'
                  }
                  if (entry.close && entry.close.length === 5) {
                    entry.close += ':00'
                  }
                  if (entry.hasLunch) {
                    if (entry.lunchStart && entry.lunchStart.length === 5) {
                      entry.lunchStart += ':00'
                    }
                    if (entry.lunchEnd && entry.lunchEnd.length === 5) {
                      entry.lunchEnd += ':00'
                    }
                  }
                }
              })

              await store.saveOpeningHours({
                regularHours: toRaw(normalHours),
                irregularHours: toRaw(irregularHours.value),
                closingDays: toRaw(closingDays.value)
              })

              await store.fetchOpeningHours()
              showDefaultNotice.value = false

              initialIrregular.value = deepClone(irregularHours.value)
              isIrregularChanged.value = false

              toast.success(i18n.global.t('openingHours.savedIrregular'))
            }

            const saveClosingDays = async () => {
              await store.saveOpeningHours({
                regularHours: toRaw(normalHours),
                irregularHours: toRaw(irregularHours.value),
                closingDays: toRaw(closingDays.value)  // ← Save the full structured objects
              })

              await store.fetchOpeningHours()
              showDefaultNotice.value = false

              initialClosings.value = deepClone(closingDays.value)
              isClosingChanged.value = false

              toast.success(i18n.global.t('openingHours.savedClosing'))
            }

            const addIrregularHour = () => {
              irregularHours.value.push({
                date: null,
                is24h: false,
                open: '',
                close: '',
                hasLunch: false,
                lunchStart: '',
                lunchEnd: ''
              })
            }

            const removeIrregularHour = (index) => {
              irregularHours.value.splice(index, 1)
            }

            const addClosingDay = () => {
              closingDays.value.push({ closingStartDate: null, closingEndDate: null, useRange: false  })
            }

            const removeClosingDay = (index) => {
              closingDays.value.splice(index, 1);
            }

            return {
                validChoice, validDate, historyRows,
                weekdays,
                normalHours,
                irregularHours,
                closingDays,
                addIrregularHour,
                removeIrregularHour,
                yearAgo,
                yearAhead,
                saveIrregularHours,
                saveRegularHours,
                addClosingDay,
                saveClosingDays,
                removeClosingDay,
                showDefaultNotice,
                isRegularChanged,
                isIrregularChanged,
                isClosingChanged,
                handleOpenToggle,
                handleIs24Toggle,
                handleLunchToggle,
                handleIrregularOpenToggle,
                handleIrregularLunchToggle,
                allClosingDatesValid
            }
        }

    }

</script>


<style scoped>
/* From when the new weekly hours apply + their history */
.oh-valid { display: flex; flex-wrap: wrap; align-items: center; gap: 6px 16px; margin: 10px 0 2px;
            padding: 10px 12px; border: 1px solid #d6e4f3; background: #f5f9fd; border-radius: 10px; }
.oh-valid-title { font-weight: 600; font-size: .9rem; color: #2C5F86; margin-right: 4px; }
.oh-valid-opt { display: inline-flex; align-items: center; gap: 6px; font-size: .9rem; }
.oh-valid-date { font-size: .85rem; padding: 2px 6px; }
.oh-valid .oh-hint { flex-basis: 100%; margin: 2px 0 0; }
.oh-history-row { display: flex; gap: 10px; align-items: baseline; padding: 4px 0; font-size: .88rem; }
.oh-history-when { min-width: 130px; color: #4F7EB3; font-weight: 600; }
.oh-history-sum { flex: 1; }
.oh-history-tag { font-size: .75rem; padding: 1px 6px; border-radius: 8px; background: #e8f3e4; color: #2f6b2a; }
.oh-history-tag.upcoming { background: #eef4fb; color: #2C5F86; }
/* Card shell — matches the redesigned Settings cards. No overflow clipping:
   the datepicker calendar popups must be free to render past the card edge. */
.setting-card {
  background: #fff;
  border: 1px solid #e6edf3;
  border-radius: 12px;
  box-shadow: 0 1px 2px rgba(13, 38, 76, 0.04);
}
.setting-card-head {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 13px 16px 12px;
  border-bottom: 1px solid #f0f3f7;
}
.sc-accent {
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  font-size: 1.05rem;
  color: var(--accent, #4F7EB3);
}
.sc-heading { min-width: 0; }
.sc-title { margin: 0; font-weight: 700; font-size: 0.98rem; color: #2b3a45; }
.sc-desc { margin: 1px 0 0; color: #7c8894; font-size: 0.8rem; line-height: 1.35; }
.setting-card-body { padding: 14px 16px; }

.alert.info {
  background: #f2f8fc;
  color: #425461;
  border-left: 3px solid #4F7EB3;
  padding: 9px 12px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.85rem;
  margin-bottom: 12px;
}

/* Section heads */
.oh-section-head { display: flex; align-items: center; margin-bottom: 8px; }
.oh-section-title {
  font-size: 0.9rem; font-weight: 700; color: #425461;
}

/* Compact one-row-per-entry list. Growth is bounded by the collapsed-by-default
   <details> wrapper, NOT by an overflow scroll-cap (which trapped the datepicker
   calendar). Expanding a section to manage it is a deliberate, temporary action. */
.oh-list { display: flex; flex-direction: column; gap: 6px; }
.oh-row {
  position: relative;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px 12px;
  padding: 8px 10px;
  background: #fbfcfd;
  border: 1px solid #eceff3;
  border-radius: 8px;
}
.oh-day {
  flex: 0 0 34px;
  font-weight: 700;
  font-size: 0.82rem;
  color: #4F7EB3;
  text-transform: uppercase;
  letter-spacing: 0.02em;
}
.oh-switch {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin: 0;
  font-size: 0.82rem;
  color: #425461;
  cursor: pointer;
}
.oh-switch.sm { font-size: 0.78rem; color: #6b7a86; }
.oh-switch .form-check-input { margin: 0; }
.oh-times { display: inline-flex; align-items: center; gap: 6px; }
.oh-lbl { font-size: 0.75rem; color: #8a97a3; }
input[type="time"] {
  width: 86px;
  padding: 3px 6px;
  font-size: 0.82rem;
  border: 1px solid #d3dae1;
  border-radius: 6px;
  color: #425461;
}
input[type="time"]:focus {
  border-color: #B2C149;
  outline: none;
  box-shadow: 0 0 0 0.15rem rgba(178, 193, 73, 0.2);
}
/* the tooltip wrapper is now the flex item, so it carries the right-edge push */
.remove-wrap {
  order: 99;
  margin-left: auto;
}
.remove {
  color: #b9424a;
  border: none;
  background: none;
  font-size: 1.1rem;
  line-height: 1;
  cursor: pointer;
  padding: 0 2px;
}
.remove:hover { color: #C21500; }

/* Collapsible exception sections */
.oh-details {
  border: 1px solid #eef2f6;
  border-radius: 10px;
  margin-top: 12px;
  background: #fff;
}
.oh-details > summary {
  list-style: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 12px;
  font-weight: 600;
  color: #425461;
  font-size: 0.9rem;
}
.oh-details > summary::-webkit-details-marker { display: none; }
.oh-details > summary .count { color: #8a97a3; font-weight: 600; }
.oh-details > summary .chev {
  margin-left: auto; color: #9aa7b3; transition: transform 0.15s ease;
}
.oh-details[open] > summary .chev { transform: rotate(90deg); }
.oh-sub-body { padding: 0 12px 12px; }
.oh-hint { font-size: 0.8rem; color: #7c8894; margin: 0 0 10px; }

.oh-foot {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 10px;
}

/* Buttons */
.btn-outline {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  border: 1px solid #4F7EB3;
  color: #4F7EB3;
  background: #fff;
  border-radius: 8px;
  padding: 7px 12px;
  cursor: pointer;
  font-size: 0.85rem;
}
.btn-outline:hover { background: #4F7EB3; color: #fff; }
.btn-solid.ok {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: #6C9046;
  color: #fff;
  border: none;
  border-radius: 8px;
  padding: 7px 12px;
  cursor: pointer;
  font-size: 0.85rem;
}
button:disabled,
.btn-solid:disabled,
.btn-outline:disabled {
  opacity: 0.5;
  cursor: not-allowed;
  pointer-events: none;
  filter: grayscale(40%);
}
.btn-solid:disabled { background: #c9cfd6 !important; color: #fff !important; }
.form-actions { display: flex; justify-content: flex-end; margin-top: 12px; }

/* Datepicker: make its input match the compact time inputs beside it, and let
   its calendar popup sit above following rows. */
.datepicker-inline { max-width: 150px; }
.datepicker-inline :deep(input) {
  width: 130px;
  padding: 3px 8px;
  font-size: 0.82rem;
  border: 1px solid #d3dae1;
  border-radius: 6px;
  color: #425461;
}
.datepicker-inline :deep(input:focus) {
  border-color: #B2C149;
  outline: none;
  box-shadow: 0 0 0 0.15rem rgba(178, 193, 73, 0.2);
}
.datepicker-inline :deep(.v3dp__datepicker) { position: relative; z-index: 5; }
</style>